import { OrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import { estimateRegionSizeBytes } from "@/modes/ipx-order-mode/ipx-size-estimate";
import { createSelector } from "@reduxjs/toolkit";
import { selectClippingBox } from "../clipping-box-selectors";
import { RootState } from "../store";
import { IpxFloorEstimate, IpxOrderModeState } from "./ipx-order-mode-slice";

/**
 * @param state The app state
 * @returns the state of the IPX ordering wizard
 */
export function selectIpxOrderMode(state: RootState): IpxOrderModeState {
  return state.ipxOrderMode;
}

/** @returns the estimated size of the selected region in bytes, or undefined while the point cloud is loading */
export const selectIpxEstimatedRegionBytes = createSelector(
  [selectIpxOrderMode, selectIpxActiveClippingBox],
  (
    {
      regionExportBytes,
      pointCloudSize,
      pointCloudTotalBytes,
      totalScanCount,
      sentScans,
    },
    clippingBox,
  ) => {
    if (regionExportBytes !== undefined) return regionExportBytes;
    if (!pointCloudSize || pointCloudTotalBytes === undefined) return;
    // Assumes the scans contribute evenly to the dataset point cloud
    const scanShare = totalScanCount ? sentScans.length / totalScanCount : 1;
    return estimateRegionSizeBytes(
      pointCloudTotalBytes * scanShare,
      pointCloudSize,
      clippingBox?.size,
    );
  },
);

/**
 * @param state The app state
 * @returns the volume of the region box in cubic meters, or undefined without a box
 */
export function selectIpxRegionVolume(state: RootState): number | undefined {
  const size =
    selectIpxActiveClippingBox(state)?.size ??
    state.ipxOrderMode.pointCloudSize;
  return size ? size[0] * size[1] * size[2] : undefined;
}

/**
 * @param state The app state
 * @returns true if leaving the wizard would discard the user selections
 */
export function selectIpxOrderHasProgress(state: RootState): boolean {
  return state.wizard.furthestStepIndex > 0 && !state.ipxOrderMode.order;
}

/**
 * @param state The app state
 * @returns how many scans are sent, e.g. "All 130" or "12 of 130", undefined if the dataset is sent as a whole
 */
export function selectIpxSentScansLabel(state: RootState): string | undefined {
  const { totalScanCount, selectedScanIds, sentScans } = state.ipxOrderMode;
  if (totalScanCount === undefined) return;
  return selectedScanIds.length
    ? `${sentScans.length} of ${totalScanCount}`
    : `All ${totalScanCount}`;
}

/**
 * @param state The app state
 * @returns the region box clipping what is sent, undefined if the whole selection is sent
 */
export function selectIpxActiveClippingBox(
  state: RootState,
): OrientedBoundingBox | undefined {
  return state.ipxOrderMode.isClippingBoxEnabled
    ? selectClippingBox(state)
    : undefined;
}

/**
 * @param state The app state
 * @returns the floor area of the region in square meters, the sum of its floors walls included,
 * the last one while the floors are detected again. The box footprint if no floor was detected.
 */
export function selectIpxRegionFloorArea(state: RootState): number | undefined {
  const { floorEstimate, pointCloudSize } = state.ipxOrderMode;
  if (!floorEstimate) return;
  const { status, floors } = floorEstimate;
  if (floors.length) return floors.reduce((sum, floor) => sum + floor.area, 0);
  if (status === "computing") return;
  // Rather than an area of zero, the worst under-estimate
  const size = selectIpxActiveClippingBox(state)?.size ?? pointCloudSize;
  // Y is up, the floor is the X/Z plane
  return size ? size[0] * size[2] : undefined;
}

/**
 * @param state The app state
 * @returns the floors detected in the region, undefined until the detection starts
 */
export function selectIpxFloorEstimate(
  state: RootState,
): IpxFloorEstimate | undefined {
  return state.ipxOrderMode.floorEstimate;
}
